import React, { useCallback } from "react"
import type { OrderItem, CheckoutStateQuery } from "@/api/graphql"
import type { MutationFunction } from "@apollo/client/react"
import ClientData from "@/common/client_data"
import { graphql } from "@/api"
import { Mutation } from "@apollo/client/react/components"
import type { OrderItemCountUpdateMutation, OrderItemCountUpdateMutationVariables } from "@/api/graphql"

const countUpdateDoc = graphql(`
  mutation OrderItemCountUpdate($id: ID!, $value: String!) {
    orderItemCountUpdate(id: $id, value: $value) {
      item {
        id
        totalCount
        localPrice
        localTaxedPrice
        localTotal
        localTaxedTotal
      }

      order {
        id
        confirmed
        numberOfSigns
        discountPercent
        discountConverted
        localDiscountAmount
        taxedDiscountAmount
        localPrice
        localTax
        localTotal
        localTotalAmount
        vatPercent
        invoiceFeesLocalTotal
        deliveryAndShippingLocalTotal
        creditItemsLocalTotal
        deliveryMethod
        deliveryMethodName
        paymentMethod
        basketItemsDescription
        basketItemsLocalTotal
        basketItemsTaxedTotal
        basketItemsRegularLocalTotal
        basketItemsRegularTaxedTotal
        package
        hasDiscount

        basketItems {
          id
          productIndex
          articleType
          description
          clientDescription
          localPrice
          localTaxedPrice
          localTotal
          localTaxedTotal
          priceSek
          totalCount
          v2
          signType
          previewClass
          previewSvgB
          previewSvg
          material
          materialTr
          width
          height
          hangType
          colors
          campaign
          data
          fabricJson
          woodEngraved
          adoptUrl
          sharePath
          additionalProduct
          metaProduct
          thickness
          itemNumber
          customerEdit
          regularLocalTotal
          regularTaxedTotal
          hasDiscount
          customProductAttributes
          customProduct
          bundleItem
          exampleFixedPrice
          discontinued
          __typename

          bundle {
            id

            image {
              id
              webp
              png
            }
          }
        }

        paymentMethods {
          id
          enabledCheckout
          enabledPayment
          businessOnly
        }

        deliveryMethods {
          id
          type
          enabled
          price
          tax
          description

          logo {
            onex
            twox
            __typename
          }
        }

        deliveryFees {
          articleTranslation
          localTotal
          __typename
        }
      }

      errors {
        message
      }
    }
  }
`)

interface ItemCountProps {
  order: CheckoutStateQuery["order"]
  item: Pick<OrderItem, "totalCount" | "localPrice" | "localTaxedPrice" | "localTotal" | "localTaxedTotal" | "id">
  onCountUpdate?: (method: string) => void
}

const ItemCount: React.FC<ItemCountProps> = ({ order, item, onCountUpdate }) => {
  const change = useCallback(
    (
      mutation: MutationFunction<OrderItemCountUpdateMutation, OrderItemCountUpdateMutationVariables>,
      value: string
    ) => {
      let expectedCount = item.totalCount

      if (value === "minus") {
        expectedCount = Math.max(1, expectedCount - 1)
      } else if (value === "plus") {
        expectedCount = expectedCount + 1
      } else {
        expectedCount = parseInt(value, 10)
      }

      mutation({
        variables: {
          id: item.id,
          value,
        },
        update: (client, { data }) => {
          if (data?.orderItemCountUpdate?.order?.deliveryMethod && onCountUpdate) {
            onCountUpdate(data.orderItemCountUpdate.order.deliveryMethod)
          }
        },
        optimisticResponse: {
          orderItemCountUpdate: {
            order: null,
            errors: null,
            item: {
              ...item,
              totalCount: expectedCount,
              __typename: "OrderItem",
            },
            __typename: "OrderItemCountUpdatePayload",
          },
        },
      })
    },
    [item, onCountUpdate]
  )

  return order.confirmed || order.cancelled || ClientData.hideUpdateButtons ? (
    <div className={`preview_item_count_${item.id} article_count`}>{item.totalCount}</div>
  ) : (
    <Mutation<OrderItemCountUpdateMutation, OrderItemCountUpdateMutationVariables> mutation={countUpdateDoc}>
      {(mutation, { loading }) => (
        <>
          <button
            className="spinners basket spinner_minus"
            onClick={() => change(mutation, "minus")}
            disabled={item.totalCount < 2 || loading}
            type="button"
          >
            -
          </button>
          <input
            type="text"
            name="basket_item_count"
            value={item.totalCount}
            className={`preview_item_count_${item.id} article_count`}
            size={5}
            width="40"
            maxLength={4}
            onChange={e => change(mutation, e.target.value)}
            onPaste={e => change(mutation, e.clipboardData.getData("Text"))}
          />
          <button
            className="spinners basket spinner_plus"
            onClick={() => change(mutation, "plus")}
            disabled={loading}
            type="button"
          >
            +
          </button>
        </>
      )}
    </Mutation>
  )
}

export default ItemCount
